import api from "./transactionApi";
import {
  DuplicateActionEnum,
  ImportSettlementDataHttpRequest,
  TransactionRepeatEnum,
  UpdateLinkageHttpRequest,
  UpsertFinancialsHttpRequest,
  UpsertTransactionHttpRequest,
} from "../../repsaApi";
import {
  transactionCreating,
  transactionCreatingError,
  transactionCreated,
  transactionDuplicate,
} from "./transactionStateActions";
import {
  DuplicateActionEnumModel,
  IAddressModel,
  ICreateTransactionModel,
  IFinancialDtoModel,
  ISettlementDataModel,
  LinkFieldEnumModel,
} from "../../../types/types";
import dayjs, { Dayjs } from "dayjs";
import { ITransactionSummary } from "../../../types/transactionSummary";

export function upsertTransaction(transaction: ICreateTransactionModel) {
  return function (dispatch: any) {
    dispatch(transactionCreating());

    const request = new UpsertTransactionHttpRequest();
    request.init({ transaction });

    api.upsertTransaction(request).then(
      (response) => {
        if (response.duplicateId) {
          dispatch(transactionDuplicate(response.duplicateId));
        } else dispatch(transactionCreated(response.id!));
      },
      (error) => dispatch(transactionCreatingError(error.message))
    );
  };
}

export function getDashboardSummary(
  yearId?: number | null
): Promise<ITransactionSummary[]> {
  return api.getTransactionsDashboardSummary(yearId);
}

export const upsertTransactionDirect = async (
  transaction: ICreateTransactionModel
) => {
  const request = new UpsertTransactionHttpRequest();
  request.init({ transaction });

  const response = await api.upsertTransaction(request);

  return response;
};

export function upsertFinancials(
  transactionId: number,
  financial: IFinancialDtoModel
) {
  const request = new UpsertFinancialsHttpRequest();
  request.init({ financial, transactionId });

  const response = api.upsertFinancials(request);

  return response;
}

export function getTransactionsByYear(
  yearId: number,
  pageIndex: number,
  pageSize: number,
  transactionTypeIds: number[] | null | undefined,
  sourceIds: number[] | null | undefined,
  reasonIds: number[] | null | undefined,
  clientIds: number[] | null | undefined,
  fromDate: dayjs.Dayjs | null | undefined,
  toDate: dayjs.Dayjs | null | undefined,
  minPrice: number | null | undefined,
  maxPrice: number | null | undefined
) {
  return api.getTransactionsSummary(
    yearId,
    pageIndex,
    pageSize,
    transactionTypeIds,
    sourceIds,
    reasonIds,
    clientIds,
    fromDate,
    toDate,
    minPrice,
    maxPrice
  );
}

export function getTransactionById(transactionId: number) {
  return api.getTransaction(transactionId);
}

export function getTransactionNavigation(
  transactionId: number,
  yearId?: number | null
) {
  return api.getTransactionNavigation(transactionId, yearId);
}

export function getFinancialDefaultData(
  transactionId: number,
  anniversaryDate?: Dayjs
) {
  return api.getFinancialDefaultData(transactionId, anniversaryDate);
}

export function updateLinkage(
  transactionId: number,
  field: LinkFieldEnumModel,
  isLinked: boolean
) {
  const request = new UpdateLinkageHttpRequest();
  request.init({
    transactionId: transactionId,
    field: field,
    isLinked: isLinked,
  });

  return api.updateLinkage(request);
}

export function insertSettlementData(
  data: ISettlementDataModel[],
  action: DuplicateActionEnumModel
) {
  const request = new ImportSettlementDataHttpRequest();
  request.init({
    settlementData: data,
    duplicateAction: action as unknown as TransactionRepeatEnum,
  });
  return api.insertSettlementData(request);
}

export function deleteTransaction(id: number) {
  return api.deleteTransaction(id);
}

export function uploadExcelFile(
  file: File,
  action: DuplicateActionEnumModel,
  enablePropMixLookup: boolean = false
) {
  return api.uploadExcelFile(
    action as unknown as DuplicateActionEnum,
    enablePropMixLookup,
    {
      data: file,
      fileName: file.name,
    }
  );
}

export function exportToExcel() {
  return api.exportExcel();
}

export async function exportFailedImports(batchId: string) {
  const response = await api.exportFailedImports(batchId);
  return response.data;
}

export function searchTransactions(
  yearIds: number[] | null | undefined,
  transactionTypeIds: number[] | null | undefined,
  sourceIds: number[] | null | undefined,
  reasonIds: number[] | null | undefined,
  areaIds: number[] | null | undefined,
  specialTagIds: number[] | null | undefined,
  specialGroupIds: number[] | null | undefined,
  coAgentId: number | null | undefined,
  zipCode: string | null | undefined,
  county: string | null | undefined,
  clientName: string | null | undefined,
  minPrice: number | null | undefined,
  maxPrice: number | null | undefined,
  address: string | null | undefined,
  minDate: dayjs.Dayjs | null | undefined,
  maxDate: dayjs.Dayjs | null | undefined,
  referralOutTypeIds: number[] | null | undefined,
  soiReferrerIds: number[] | null | undefined,
  agentReferrerIds: number[] | null | undefined,
  vendorReferrerIds: number[] | null | undefined
) {
  return api.searchTransactions(
    yearIds ?? [],
    transactionTypeIds,
    sourceIds,
    reasonIds,
    areaIds,
    specialTagIds,
    specialGroupIds,
    referralOutTypeIds,
    coAgentId,
    zipCode,
    county,
    clientName,
    minPrice,
    maxPrice,
    address,
    minDate,
    maxDate,
    soiReferrerIds,
    agentReferrerIds,
    vendorReferrerIds
  );
}

// export function searchTransactionsPaged(
//   yearIds: number[] | undefined,
//   transactionTypeIds: number[] | undefined,
//   sourceIds: number[] | undefined,
//   reasonIds: number[] | undefined,
//   clientName?: string,
//   minAmount?: number,
//   maxAmount?: number,
//   fromDate?: Dayjs,
//   toDate?: Dayjs,
//   address?: IAddressModel,
//   pageIndex?: number,
//   pageSize?: number
// ) {
//   return api.searchTransactionsWithPaging(
//     yearIds ?? [],
//     transactionTypeIds,
//     sourceIds,
//     reasonIds,
//     clientName,
//     minAmount,
//     maxAmount,
//     address?.formattedAddress ?? undefined,
//     address?.city ?? undefined,
//     address?.state ?? undefined,
//     address?.street ?? undefined,
//     address?.zip ?? undefined,
//     address?.latitude ?? undefined,
//     address?.longitude ?? undefined,
//     address?.country ?? undefined,
//     address?.county ?? undefined,
//     // fromDate,
//     // toDate,
//     // pageIndex,
//     // pageSize
//   );
// }

// export function searchTransactionsMultiple(
//   yearIds: number[] | undefined,
//   transactionTypeIds: number[] | undefined,
//   sourceIds: number[] | undefined,
//   reasonIds: number[] | undefined,
//   clientName?: string,
//   minAmount?: number,
//   maxAmount?: number,
//   fromDate?: Dayjs,
//   toDate?: Dayjs,
//   address?: IAddressModel
// ) {
//   return api.searchTransactions(
//     yearIds && yearIds.length > 0 ? yearIds : undefined,
//     transactionTypeIds && transactionTypeIds.length > 0
//       ? transactionTypeIds
//       : undefined,
//     sourceIds && sourceIds.length > 0 ? sourceIds : undefined,
//     reasonIds && reasonIds.length > 0 ? reasonIds : undefined,
//     clientName,
//     minAmount,
//     maxAmount,
//     // fromDate,
//     // toDate,
//     address
//   );
// }
// export function getTransactionRepeats(
//   entity: TransactionRepeatEnumModel
// ): Promise<IEntityRepeatModel[]> {
//   const enumValue = entity as unknown as TransactionRepeatEnum;
//   return api.getTransactionRepeats(enumValue);
// }

// export function getTransactionsByEntity(
//   entity: TransactionRepeatEnumModel,
//   entityId: number
// ) {
//   const enumValue = entity as unknown as TransactionRepeatEnum;
//   return api.getTransactionsByEntity(enumValue, entityId);
// }
